// Global Zustand store.
//
// Slice ownership:
//   • S2 baseline: agents map, selectedAgentId, concurrency, connectionStatus.
//   • S3: searchQuery slice + agent CRUD (load/pin/archive/
//     markRead/markUnread/rename/delete) + filteredAgents selector.
//   • S4: messagesByAgent, partialTextByAgent, userScrolledByAgent +
//     appendMessage / setMessages / setPartialText / clearPartial /
//     loadMessages / setUserScrolled.
//   • Sprint 5.6: composer-first slice — composerOpen + composerDraft.
//     The templates slice was deleted (ADR D-001).
//   • Sprint UI-1: removed `currentView` slice and the `archived` flag on
//     `loadAgents` (Layer 5 D14 — single sectioned list, archived becomes
//     a section, not a separate view). `selectFilteredAgents` now filters
//     non-archived rows only; UI-3 replaces it with `selectGroupedAgents`.
//
// `removeAgent` is the pure-local mutation used by `useSocket` when the
// backend emits `agent:deleted`. `deleteAgent` is the user-action variant:
// it calls the REST API (which kills the process + soft-deletes) and the
// resulting `agent:deleted` socket event funnels back through `removeAgent`.

import { create } from 'zustand';
import type {
  Agent,
  Concurrency,
  ConnectionStatus,
  EffortLevel,
  LiveActivity,
  Message,
  PermissionMode,
  RateLimitState,
} from '../types';
import { api, createEmptyAgent, fetchAgentMessages } from '../lib/api';
import { DEFAULT_EFFORT, DEFAULT_MODEL, reconcileEffort } from '../lib/models';
import { getSocket } from '../lib/socket';
import { useSettingsStore } from './settings.store';

// Sprint 11b.3 (D-009) — optimistic agent creation. The "+" button
// renders a usable composer immediately by inserting a row with a
// `tmp-<uuid>` id and a `pending=true` flag, then firing the real
// `POST /api/agents/empty` in the background. When the response lands,
// `replacePendingWithReal` swaps the temp row for the real one
// atomically and updates `selectedAgentId` if the user is still
// looking at the temp id. The createPromise is exposed via
// `getPendingPromise(tempId)` so `ChatView.handleSend` can await it
// before pushing the user's first message — see OAC4.
const TMP_ID_PREFIX = 'tmp-';
export function isTempAgentId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith(TMP_ID_PREFIX);
}
function makeTempId(): string {
  // crypto.randomUUID is in every browser the dashboard supports
  // (PWA-installable on iOS Safari 16+ where it has been available
  // since 16.0). The collision probability is negligible, so we don't
  // guard against duplicates here (OAC7).
  return TMP_ID_PREFIX + crypto.randomUUID();
}

export interface CreateOptimisticOpts {
  projectId?: string;
  permissionMode?: PermissionMode;
  model?: string;
  effort?: EffortLevel;
  // Sprint runner-picker-on-create — optional pin. Omitted = Auto
  // (backend scheduler picks). Stamped on the placeholder row only
  // when pinned, so the Auto path does not flicker a chip.
  runnerId?: string;
  // Defaults to a "New conversation" placeholder. Tests can override.
  placeholderName?: string;
}

interface PendingEntry {
  createPromise: Promise<Agent>;
}

// Sprint 5.6 — composer-first draft. Held in-memory until the user sends
// the first message; on send we POST /api/agents and the row materialises
// via the agent:created socket event. Never persisted — refreshing kills
// the draft, which matches ChatGPT/Cursor convention.
export interface ComposerDraft {
  prompt: string;
  model: string;
  effort: EffortLevel;
  // S6.5 — Mode chip default for the next agent. Persisted only in this
  // draft slice; the value lands in the createAgent body on send and the
  // backend stores it on the agent row.
  permissionMode: PermissionMode;
}

// Sprint UI-7b (D76) — server identity surfaced by `/api/health`. Used by
// the ChatHeader "server" chip so Santi knows which machine this agent is
// running on (important once we're federated across 2-3 VPS boxes).
// `null` before the first health fetch completes; the chip falls back to
// a hardcoded label meanwhile so the header layout is stable.
export interface ServerInfo {
  serverName: string;
}

export interface AppState {
  // ---- S2 baseline ---------------------------------------------------------
  agents: Record<string, Agent>;
  selectedAgentId: string | null;
  concurrency: Concurrency;
  connectionStatus: ConnectionStatus;

  // ---- Sprint UI-7b server info slice --------------------------------------
  serverInfo: ServerInfo | null;
  loadServerInfo: () => Promise<void>;

  // ---- Sprint 20.3 PR-10 (D17) — centralized usage modal global open state.
  // Lifted out of `Sidebar`'s local `useState` so the EC-43 toast can
  // open the modal from anywhere ("View runner usage" action).
  usageModalOpen: boolean;
  setUsageModalOpen: (open: boolean) => void;

  // ---- S3 sidebar slice ----------------------------------------------------
  searchQuery: string;

  // ---- S4 messages slice ---------------------------------------------------
  messagesByAgent: Record<string, Message[]>;
  partialTextByAgent: Record<string, string>;
  // Sprint 20.3 PR-7 (D013-EC-22) — per-agent latch set by the
  // `agent:text_truncated` socket event. The chat composer renders
  // `[truncated · reload to refetch]` while this is true. Cleared by
  // any of: a fresh assistant `agent:message` arriving (the persisted
  // turn lands), `agent:updated` flipping `status` to anything other
  // than `running`, or `clearTruncated(agentId)` (composer affordance).
  streamTruncatedByAgent: Record<string, true>;
  // Sprint 17 — live in-flight indicator per agent. `undefined` = idle
  // (component renders null). Single source of truth driven by the
  // `agent:activity` socket event; never persisted.
  liveActivityByAgent: Record<string, LiveActivity>;
  userScrolledByAgent: Record<string, boolean>;

  // ---- Sprint 15 history pagination slice ---------------------------------
  // Per-agent state driving the lazy upward-pagination + sticky anchor
  // invariant. Set by loadMessages on initial fetch, mutated by
  // loadOlderMessages and ensureStickyAnchorLoaded.
  historyByAgent: Record<
    string,
    { hasMore: boolean; lastUserMessageId: string | null; loadingOlder: boolean }
  >;

  // ---- Sprint UI-7 → rate-limit-banner-ssot — banner state slice ----------
  // Banner is now a backend-derived projection of real Anthropic usage,
  // not a per-event flash from the CLI. `active=true` carries the full
  // list of (runner, bucket) currently capped so the banner can render
  // per-runner copy. Cleared automatically by the broadcaster when the
  // ground-truth usage drops below 100%.
  rateLimitState: RateLimitState;

  // ---- Sprint 20.3 PR-5 — auto-clone preparing slice ----------------------
  // Per-agent runner-side clone state. Set by the `agent:preparing`
  // socket event (`SpawnPreparingToast` consumes it). Lifecycle:
  //   - phase 'cloning' → entry set with startedAt = Date.now() so the
  //     toast can apply the §F 5-second grace (avoid flash on
  //     `already_present` runs that resolve in < 200 ms).
  //   - phase 'ready'   → entry deleted (toast dismisses).
  //   - phase 'failed'  → entry mutated; toast renders the founder-mode
  //     copy from the backend (`message`) + a Retry control.
  preparingByAgent: Record<
    string,
    | {
        runnerId: string;
        phase: 'cloning';
        startedAt: number;
      }
    | {
        runnerId: string;
        phase: 'failed';
        reason:
          | 'auth_failed'
          | 'origin_unreachable'
          | 'corrupted_existing'
          | 'disk_full'
          | 'timeout'
          | 'runner_offline';
        message: string;
        startedAt: number;
      }
  >;

  // ---- Sprint 5.6 composer slice ------------------------------------------
  composerOpen: boolean;
  composerDraft: ComposerDraft;
  openComposer: () => void;
  closeComposer: () => void;
  setComposerPrompt: (prompt: string) => void;
  setComposerModel: (model: string) => void;
  setComposerEffort: (effort: EffortLevel) => void;
  setComposerPermissionMode: (mode: PermissionMode) => void;
  resetComposer: () => void;

  // ---- Sprint messages-section-fixes (D-msg-1) — Edit on user msgs --------
  // When the founder clicks Edit on a user message, the message text is
  // stashed here keyed by agent id. The continue Composer for that
  // agent reads the value on next render via `consumeComposerInjection`,
  // imports it into its local `text` state, and clears the slot. One-
  // shot: the value is consumed exactly once.
  pendingComposerInjection: Record<string, string>;
  injectComposerText: (agentId: string, text: string) => void;
  consumeComposerInjection: (agentId: string) => string | null;

  // S2 — agent map mutations (used both by socket sync and by S3 actions).
  upsertAgent: (agent: Agent) => void;
  upsertAgents: (agents: Agent[]) => void;
  removeAgent: (id: string) => void;
  selectAgent: (id: string | null) => void;
  setConcurrency: (c: Concurrency) => void;
  setConnectionStatus: (s: ConnectionStatus) => void;

  // S3 — search slice.
  setSearchQuery: (q: string) => void;

  // S3 — agent CRUD actions (talk to the backend, optimistic where useful).
  loadAgents: () => Promise<void>;
  pinAgent: (id: string, pinned: boolean) => Promise<void>;
  archiveAgent: (id: string, archived: boolean) => Promise<void>;
  // Sprint agent-stages — move an agent to a workflow stage (null = "Sin
  // estado"). Optimistic like pinAgent; persists via the same PATCH.
  setAgentStage: (id: string, stageId: string | null) => Promise<void>;
  // Sprint agent-stages — local-only sweep after a stage deletion: the
  // backend already cleared the rows (DELETE /api/stages/:id is
  // transactional); this keeps the in-memory mirror in sync without a
  // full reload.
  clearAgentsStageLocally: (stageId: string) => void;
  renameAgent: (id: string, name: string) => Promise<void>;
  markRead: (id: string) => void;
  markUnread: (id: string) => Promise<void>;
  deleteAgent: (id: string) => Promise<void>;

  // Sprint 11b.3 (D-009) — optimistic agent creation. Returns the temp id
  // that the caller should immediately `selectAgent()` on so the composer
  // opens against the placeholder row. The real agent replaces it in the
  // map (and selectedAgentId, if still pointing at the temp id) when the
  // POST resolves; on failure the temp row is removed and the returned
  // promise rejects.
  createAgentOptimistic: (opts?: CreateOptimisticOpts) => { tempId: string; promise: Promise<Agent> };
  // Awaited by ChatView before sending the first message on a still-
  // pending agent. Returns null when the id is real (or unknown).
  getPendingPromise: (id: string) => Promise<Agent> | null;

  // Sprint rate-limit-banner-ssot — replaces the legacy `setRateLimit`.
  // Frontend never derives banner state locally; it just reflects what
  // the backend broadcaster emitted.
  setRateLimitState: (state: RateLimitState) => void;

  // ---- Sprint 20.3 PR-6 → auto-failover-on-rate-limit (D013-EC-109) ------
  // Per-agent banner state for the `RunnerUnavailableBanner` (renamed
  // from `RunnerOfflineBanner`). Set when `sendAgentInput` rejects with
  // a backend AppError code the user can recover from by reassigning
  // the conversation to a different runner — currently `RUNNER_OFFLINE`
  // (variant 'offline') or `RUNNER_RATE_LIMITED` (variant 'rate_limited').
  // Cleared when the user dismisses, when failover succeeds, or when
  // the agent is deleted. Pure UI signal — the backend is the source of
  // truth (agent.runnerId / previousRunnerId).
  runnerUnavailableByAgent: Record<
    string,
    {
      runnerId: string;
      lastErrorMessage: string;
      variant: 'offline' | 'rate_limited';
    }
  >;
  setRunnerUnavailable: (
    agentId: string,
    runnerId: string,
    lastErrorMessage: string,
    variant: 'offline' | 'rate_limited',
  ) => void;
  clearRunnerUnavailable: (agentId: string) => void;

  // Sprint 20.5 PR-2 (D013-EC-68) — per-agent SESSION_LOST latch. Set
  // when the runner emits `agent.error({code: 'SESSION_LOST'})` (i.e.
  // the runner could not respawn the Claude child to forward a queued
  // user_message). Cleared when the user dismisses, when a fresh
  // `agent:created`/`agent:updated` arrives that flips the agent back
  // to a non-error state, or when the agent is deleted. The frontend
  // reads this map to render `SessionLostBanner` above the composer.
  sessionLostByAgent: Record<string, { recoverable: boolean; message: string | null }>;
  setSessionLost: (agentId: string, recoverable: boolean, message: string | null) => void;
  clearSessionLost: (agentId: string) => void;

  // Sprint 20.6 PR-2 (D013-EC-72) — SESSION_EXPIRED latch. Set by
  // `useSocket` when the runner emits
  // `agent.error({code: 'SESSION_EXPIRED'})` (i.e. respawn-on-startup
  // tried `--resume <sessionId>` and Anthropic returned not-found).
  // Cleared when the user dismisses or starts a new agent. The
  // SessionExpiredBanner reads this map and renders above the
  // composer with a "Start a new agent" CTA — the previous turns
  // remain visible (DB-persisted), but resume is impossible because
  // the model has lost its chained context.
  sessionExpiredByAgent: Record<string, { recoverable: boolean; message: string | null }>;
  setSessionExpired: (agentId: string, recoverable: boolean, message: string | null) => void;
  clearSessionExpired: (agentId: string) => void;

  // Sprint 20.3 PR-5 — auto-clone preparing slice mutations. The
  // socket handler is the only consumer; tests pin to these directly.
  startPreparing: (agentId: string, runnerId: string) => void;
  failPreparing: (
    agentId: string,
    runnerId: string,
    reason:
      | 'auth_failed'
      | 'origin_unreachable'
      | 'corrupted_existing'
      | 'disk_full'
      | 'timeout'
      | 'runner_offline',
    message: string,
  ) => void;
  clearPreparing: (agentId: string) => void;

  // S4 — messages slice actions.
  appendMessage: (agentId: string, message: Message) => void;
  /** Cambio del estado de entrega de un mensaje ya en la lista (`agent:message_delivery`). */
  setMessageDelivery: (agentId: string, messageId: string, delivery: NonNullable<Message['delivery']>) => void;
  setMessages: (agentId: string, messages: Message[]) => void;
  setPartialText: (agentId: string, text: string) => void;
  clearPartial: (agentId: string) => void;
  // Sprint 20.3 PR-7 (D013-EC-22) — set / clear the truncation latch.
  // `setStreamTruncated` flips it to `true` (idempotent); `clearStream
  // Truncated` removes the agent from the map. Both are wired in
  // useSocket: `setStreamTruncated` from `agent:text_truncated`,
  // `clearStreamTruncated` from `agent:message` arrival.
  setStreamTruncated: (agentId: string) => void;
  clearStreamTruncated: (agentId: string) => void;
  // Sprint 17 — set the live activity for an agent (kind 'thinking' | 'tool')
  // or clear it (`null`). Mutated only by the `agent:activity` socket
  // handler. Cleared on socket disconnect to avoid stale "Working · 4:32"
  // labels after a reconnect.
  setLiveActivity: (agentId: string, activity: LiveActivity | null) => void;
  clearAllLiveActivity: () => void;
  setUserScrolled: (agentId: string, scrolled: boolean) => void;
  loadMessages: (agentId: string) => Promise<void>;

  // Sprint 15 — upward pagination + sticky anchor invariant.
  // `prependMessages` merges older pages into the loaded set, dedup by id,
  // tail order preserved. `loadOlderMessages` fetches the next page using
  // the composite cursor, single-flight per agent. `ensureStickyAnchorLoaded`
  // paginates upward in a tight loop until `lastUserMessageId` is in the
  // loaded set or `hasMore` is false (HIST7 runaway-turn safety net).
  prependMessages: (agentId: string, older: Message[]) => void;
  // 05-11 (#276, VIV-1/VIV-7) — junta mensajes traídos del servidor (el turno en curso de un agente, pedido desde ahora
  // hacia atrás) con los que ya hay: sin repetir (por id) y en el orden del servidor (timestamp, id). A diferencia de
  // `prependMessages`, lo traído puede ir intercalado (el hueco de un corte del socket) o por delante de lo que llegó en
  // vivo mientras se pedía.
  mergeMessages: (agentId: string, messages: Message[]) => void;
  loadOlderMessages: (agentId: string) => Promise<void>;
  ensureStickyAnchorLoaded: (agentId: string) => Promise<void>;

  resetForTests: () => void;
}

function makeEmptyDraft(): ComposerDraft {
  return {
    prompt: '',
    model: DEFAULT_MODEL,
    effort: DEFAULT_EFFORT,
    permissionMode: useSettingsStore.getState().defaultPermissionMode,
  };
}

const initialState = {
  agents: {} as Record<string, Agent>,
  selectedAgentId: null as string | null,
  concurrency: { running: 0, max: 0, memoryConstrained: false } as Concurrency,
  connectionStatus: 'disconnected' as ConnectionStatus,
  searchQuery: '',
  messagesByAgent: {} as Record<string, Message[]>,
  partialTextByAgent: {} as Record<string, string>,
  // Sprint 20.3 PR-7 (D013-EC-22) — empty by default; populated by the
  // `agent:text_truncated` socket event when a turn's live stream
  // crosses the backpressure cap.
  streamTruncatedByAgent: {} as Record<string, true>,
  liveActivityByAgent: {} as Record<string, LiveActivity>,
  userScrolledByAgent: {} as Record<string, boolean>,
  historyByAgent: {} as Record<
    string,
    { hasMore: boolean; lastUserMessageId: string | null; loadingOlder: boolean }
  >,
  composerOpen: false,
  composerDraft: makeEmptyDraft(),
  pendingComposerInjection: {} as Record<string, string>,
  rateLimitState: { active: false } as RateLimitState,
  preparingByAgent: {} as AppState['preparingByAgent'],
  runnerUnavailableByAgent: {} as AppState['runnerUnavailableByAgent'],
  sessionLostByAgent: {} as AppState['sessionLostByAgent'],
  // Sprint 20.6 PR-2 (D013-EC-72) — SESSION_EXPIRED latch.
  sessionExpiredByAgent: {} as AppState['sessionExpiredByAgent'],
  serverInfo: null as ServerInfo | null,
  usageModalOpen: false,
};

interface AgentListResponse {
  agents: Agent[];
  concurrency: Concurrency;
}
interface AgentResponse {
  agent: Agent;
}

// Per-store map of temp-id → in-flight create promise. Lives outside
// zustand state so React renders never depend on the promise reference
// changing. Read by `getPendingPromise(tempId)` and cleared in
// replacePendingWithReal / removePending.
const pendingAgents = new Map<string, PendingEntry>();

export const useAppStore = create<AppState>((set, get) => ({
  ...initialState,

  // ---- Sprint 5.6 composer slice ------------------------------------------

  openComposer: () =>
    set({
      composerOpen: true,
      // Composer takes over the chat surface, so deselect any current agent.
      // The chat view reacts to selectedAgentId === null + composerOpen.
      selectedAgentId: null,
    }),

  closeComposer: () => set({ composerOpen: false, composerDraft: makeEmptyDraft() }),

  setComposerPrompt: (prompt) =>
    set((s) => ({ composerDraft: { ...s.composerDraft, prompt } })),

  setComposerModel: (model) =>
    set((s) => ({
      composerDraft: {
        ...s.composerDraft,
        model,
        effort: reconcileEffort(model, s.composerDraft.effort),
      },
    })),

  setComposerEffort: (effort) =>
    set((s) => ({ composerDraft: { ...s.composerDraft, effort } })),

  setComposerPermissionMode: (permissionMode) =>
    set((s) => ({ composerDraft: { ...s.composerDraft, permissionMode } })),

  resetComposer: () => set({ composerDraft: makeEmptyDraft() }),

  // Sprint messages-section-fixes (D-msg-1) — one-shot inject of text
  // into a continue-composer's local state (used by Edit on user msgs).
  // Read via the create-time `get()` (not `useAppStore.getState()`) so
  // the store's type inference does not become circular at definition.
  injectComposerText: (agentId, text) =>
    set((s) => ({
      pendingComposerInjection: { ...s.pendingComposerInjection, [agentId]: text },
    })),
  consumeComposerInjection: (agentId): string | null => {
    const value = get().pendingComposerInjection[agentId] ?? null;
    if (value !== null) {
      set((s) => {
        const next = { ...s.pendingComposerInjection };
        delete next[agentId];
        return { pendingComposerInjection: next };
      });
    }
    return value;
  },

  upsertAgent: (agent) =>
    set((s) => ({ agents: { ...s.agents, [agent.id]: agent } })),

  upsertAgents: (agents) =>
    set((s) => {
      const next = { ...s.agents };
      for (const a of agents) next[a.id] = a;
      return { agents: next };
    }),

  removeAgent: (id) =>
    set((s) => {
      if (!(id in s.agents)) return s;
      const next = { ...s.agents };
      delete next[id];
      // Drop S4 agent-scoped slices too — no point holding the chat history,
      // partial buffer, or scroll flag for an agent the server says is gone.
      const nextMessages = { ...s.messagesByAgent };
      delete nextMessages[id];
      const nextPartial = { ...s.partialTextByAgent };
      delete nextPartial[id];
      const nextScroll = { ...s.userScrolledByAgent };
      delete nextScroll[id];
      return {
        agents: next,
        messagesByAgent: nextMessages,
        partialTextByAgent: nextPartial,
        userScrolledByAgent: nextScroll,
        selectedAgentId: s.selectedAgentId === id ? null : s.selectedAgentId,
      };
    }),

  selectAgent: (id) => set({ selectedAgentId: id, composerOpen: false }),

  // S7.4 — defensive against contract drift. The bare-stats backend bug
  // (now fixed) handed us `undefined` here and threw `Cannot read properties
  // of undefined ('memoryConstrained')` from every component that read
  // `s.concurrency.memoryConstrained`, blowing up the React tree to a black
  // screen. Even with the backend fixed, refusing to commit a malformed
  // payload keeps the next future drift from doing the same.
  setConcurrency: (concurrency) => {
    if (
      !concurrency ||
      typeof concurrency.running !== 'number' ||
      // Sprint 11a — `max` is `number | null`. null = no static cap (the
      // new default after MAX_CONCURRENT_AGENTS removal). Both shapes are
      // valid; any other type is contract drift and we drop it.
      (concurrency.max !== null && typeof concurrency.max !== 'number') ||
      typeof concurrency.memoryConstrained !== 'boolean'
    ) {
      return;
    }
    set({ concurrency });
  },

  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),

  setRateLimitState: (rateLimitState) => set({ rateLimitState }),

  setUsageModalOpen: (open) => set({ usageModalOpen: open }),

  startPreparing: (agentId, runnerId) =>
    set((state) => ({
      preparingByAgent: {
        ...state.preparingByAgent,
        [agentId]: { runnerId, phase: 'cloning', startedAt: Date.now() },
      },
    })),
  failPreparing: (agentId, runnerId, reason, message) =>
    set((state) => {
      const prev = state.preparingByAgent[agentId];
      const startedAt = prev?.startedAt ?? Date.now();
      return {
        preparingByAgent: {
          ...state.preparingByAgent,
          [agentId]: { runnerId, phase: 'failed', reason, message, startedAt },
        },
      };
    }),
  clearPreparing: (agentId) =>
    set((state) => {
      if (!state.preparingByAgent[agentId]) return state;
      const { [agentId]: _drop, ...rest } = state.preparingByAgent;
      return { preparingByAgent: rest };
    }),

  setRunnerUnavailable: (agentId, runnerId, lastErrorMessage, variant) =>
    set((state) => ({
      runnerUnavailableByAgent: {
        ...state.runnerUnavailableByAgent,
        [agentId]: { runnerId, lastErrorMessage, variant },
      },
    })),
  clearRunnerUnavailable: (agentId) =>
    set((state) => {
      if (!state.runnerUnavailableByAgent[agentId]) return state;
      const { [agentId]: _drop, ...rest } = state.runnerUnavailableByAgent;
      return { runnerUnavailableByAgent: rest };
    }),

  // Sprint 20.5 PR-2 (D013-EC-68) — SESSION_LOST latch mutations.
  setSessionLost: (agentId, recoverable, message) =>
    set((state) => ({
      sessionLostByAgent: {
        ...state.sessionLostByAgent,
        [agentId]: { recoverable, message },
      },
    })),
  clearSessionLost: (agentId) =>
    set((state) => {
      if (!state.sessionLostByAgent[agentId]) return state;
      const { [agentId]: _drop, ...rest } = state.sessionLostByAgent;
      return { sessionLostByAgent: rest };
    }),

  // Sprint 20.6 PR-2 (D013-EC-72) — SESSION_EXPIRED latch mutations.
  setSessionExpired: (agentId, recoverable, message) =>
    set((state) => ({
      sessionExpiredByAgent: {
        ...state.sessionExpiredByAgent,
        [agentId]: { recoverable, message },
      },
    })),
  clearSessionExpired: (agentId) =>
    set((state) => {
      if (!state.sessionExpiredByAgent[agentId]) return state;
      const { [agentId]: _drop, ...rest } = state.sessionExpiredByAgent;
      return { sessionExpiredByAgent: rest };
    }),

  // Sprint UI-7b (D76) — fetch `/api/health` once at boot and cache the
  // server identity. The endpoint is public (no auth) so this works even
  // on the login screen. Sprint 11b: uses the active server's base URL.
  loadServerInfo: async () => {
    try {
      const { getActiveBaseUrl } = await import('./servers.store');
      const base = getActiveBaseUrl();
      const res = await fetch(`${base}/api/health`, { credentials: 'include' });
      if (!res.ok) return;
      const body = (await res.json()) as { serverName?: string };
      if (typeof body.serverName === 'string' && body.serverName.trim().length > 0) {
        set({ serverInfo: { serverName: body.serverName.trim() } });
      }
    } catch {
      // Network error — leave serverInfo null; the chip uses its default.
    }
  },

  // ---- S3 sidebar slice ----------------------------------------------------

  setSearchQuery: (searchQuery) => set({ searchQuery }),

  // Sprint UI-7: single call returns all non-deleted agents (archived and
  // non-archived). `useGroupedAgents` sections them client-side.
  loadAgents: async () => {
    const res = await api<AgentListResponse>(`/api/agents`);
    set((s) => {
      const next = { ...s.agents };
      for (const a of res.agents) next[a.id] = a;
      return { agents: next, concurrency: res.concurrency };
    });
  },

  pinAgent: async (id, pinned) => {
    const prev = get().agents[id];
    if (prev) set((s) => ({ agents: { ...s.agents, [id]: { ...prev, pinned } } }));
    const { agent } = await api<AgentResponse>(`/api/agents/${id}`, {
      method: 'PATCH',
      body: { pinned },
    });
    set((s) => ({ agents: { ...s.agents, [id]: agent } }));
  },

  setAgentStage: async (id, stageId) => {
    const prev = get().agents[id];
    if (prev) set((s) => ({ agents: { ...s.agents, [id]: { ...prev, stageId } } }));
    const { agent } = await api<AgentResponse>(`/api/agents/${id}`, {
      method: 'PATCH',
      body: { stageId },
    });
    set((s) => ({ agents: { ...s.agents, [id]: agent } }));
  },

  clearAgentsStageLocally: (stageId) => {
    set((s) => {
      const next = { ...s.agents };
      for (const agentId in next) {
        const a = next[agentId];
        if (a?.stageId === stageId) next[agentId] = { ...a, stageId: null };
      }
      return { agents: next };
    });
  },

  archiveAgent: async (id, archived) => {
    const prev = get().agents[id];
    if (prev)
      set((s) => ({
        agents: {
          ...s.agents,
          [id]: { ...prev, archived, pinned: archived ? false : prev.pinned },
        },
      }));
    const { agent } = await api<AgentResponse>(`/api/agents/${id}`, {
      method: 'PATCH',
      body: { archived },
    });
    set((s) => ({ agents: { ...s.agents, [id]: agent } }));
  },

  renameAgent: async (id, name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const prev = get().agents[id];
    if (prev)
      set((s) => ({ agents: { ...s.agents, [id]: { ...prev, name: trimmed } } }));
    const { agent } = await api<AgentResponse>(`/api/agents/${id}`, {
      method: 'PATCH',
      body: { name: trimmed },
    });
    set((s) => ({ agents: { ...s.agents, [id]: agent } }));
  },

  // markRead is the "I just opened this agent" path. We emit over the
  // singleton socket (server is idempotent — case E4) AND optimistically
  // clear the unread flag so the sidebar reflects it before the server's
  // agent:updated round-trip arrives.
  markRead: (id) => {
    const prev = get().agents[id];
    if (prev && prev.unread) {
      set((s) => ({ agents: { ...s.agents, [id]: { ...prev, unread: false } } }));
    }
    try {
      getSocket().emit('agent:read', { agentId: id });
    } catch {
      // Socket not initialised yet (e.g. during tests). The next reconnect
      // will not re-mark — that is fine, the optimistic state is good enough
      // and a manual mark-as-unread + re-open would re-trigger.
    }
  },

  markUnread: async (id) => {
    const prev = get().agents[id];
    if (prev)
      set((s) => ({ agents: { ...s.agents, [id]: { ...prev, unread: true } } }));
    const { agent } = await api<AgentResponse>(`/api/agents/${id}`, {
      method: 'PATCH',
      body: { unread: true },
    });
    set((s) => ({ agents: { ...s.agents, [id]: agent } }));
  },

  deleteAgent: async (id) => {
    await api<void>(`/api/agents/${id}`, { method: 'DELETE' });
    // The backend will emit `agent:deleted` which `useSocket` mirrors via
    // `removeAgent`; do it immediately for snappier UX.
    get().removeAgent(id);
  },

  // Sprint 11b.3 (D-009) — see CreateOptimisticOpts.
  createAgentOptimistic: (opts = {}) => {
    const tempId = makeTempId();
    const nowIso = new Date().toISOString();
    const placeholder: Agent = {
      id: tempId,
      name: opts.placeholderName ?? 'New conversation',
      // 'waiting' is the legitimate Idle state under D-008 (no CLI alive
      // yet). The frontend collapses it to "Idle" already; the visual
      // pending treatment comes from `pending: true` below, not from a
      // new status enum value.
      status: 'waiting',
      model: opts.model ?? DEFAULT_MODEL,
      effort: opts.effort ?? DEFAULT_EFFORT,
      claudeSessionId: null,
      pinned: false,
      archived: false,
      unread: false,
      deleted: false,
      createdAt: nowIso,
      lastActivityAt: nowIso,
      initialPrompt: '',
      workdir: '',
      exitCode: null,
      errorMessage: null,
      contextUsedPct: null,
      gitStatus: null,
      deletedAt: null,
      permissionMode: opts.permissionMode ?? 'agent',
      lastAgentSummary: null,
      projectId: opts.projectId,
      // Sprint runner-picker-on-create — only stamp the runner on the
      // placeholder when the user explicitly pinned one. The Auto path
      // leaves it undefined so the chip does not flicker before the
      // backend tells us which runner the scheduler picked.
      runnerId: opts.runnerId,
      pending: true,
    };
    set((s) => ({ agents: { ...s.agents, [tempId]: placeholder } }));

    const promise: Promise<Agent> = createEmptyAgent({
      projectId: opts.projectId,
      permissionMode: opts.permissionMode,
      model: opts.model,
      effort: opts.effort,
      runnerId: opts.runnerId,
    }).then(
      (real) => {
        // Atomic swap: drop the temp row, insert the real one. If the
        // user is still on the temp id, point them at the real agent so
        // the composer doesn't suddenly open on an empty selection.
        pendingAgents.delete(tempId);
        set((s) => {
          const next = { ...s.agents };
          delete next[tempId];
          next[real.id] = real;
          return {
            agents: next,
            selectedAgentId: s.selectedAgentId === tempId ? real.id : s.selectedAgentId,
          };
        });
        return real;
      },
      (err: unknown) => {
        // OAC5/OAC6: failure path. Remove the temp row + clear the
        // selection if the user is still looking at it. The caller
        // (Sidebar) shows the toast — we keep the store side concerned
        // only with state, not UI.
        pendingAgents.delete(tempId);
        set((s) => {
          if (!(tempId in s.agents)) return s;
          const next = { ...s.agents };
          delete next[tempId];
          return {
            agents: next,
            selectedAgentId: s.selectedAgentId === tempId ? null : s.selectedAgentId,
          };
        });
        throw err;
      },
    );

    pendingAgents.set(tempId, { createPromise: promise });
    return { tempId, promise };
  },

  getPendingPromise: (id) => {
    if (!isTempAgentId(id)) return null;
    return pendingAgents.get(id)?.createPromise ?? null;
  },

  // ---- S4 messages slice ---------------------------------------------------

  setMessageDelivery: (agentId, messageId, delivery) =>
    set((s) => {
      const lista = s.messagesByAgent[agentId];
      if (!lista || !lista.some((m) => m.id === messageId)) return s;
      return {
        messagesByAgent: {
          ...s.messagesByAgent,
          [agentId]: lista.map((m) => (m.id === messageId ? { ...m, delivery } : m)),
        },
      };
    }),
  appendMessage: (agentId, message) =>
    set((s) => {
      const existing = s.messagesByAgent[agentId] ?? [];
      // De-dup by id — backend message ids are unique. Protects against double
      // emission if a tab reconnects mid-stream and we replay.
      if (existing.some((m) => m.id === message.id)) return s;
      const next = [...existing, message];
      // When a real assistant_text message arrives, the live partial buffer
      // for that agent should clear (the streamed tokens are now persisted
      // as a real message). Also clear on tool_use which marks "this turn
      // finished its prose, moving to a tool call".
      const partial = { ...s.partialTextByAgent };
      if (message.type === 'assistant_text' || message.type === 'tool_use') {
        delete partial[agentId];
      }
      // Sprint 20.3 PR-7 (D013-EC-22) — clear the truncation latch when
      // a real assistant_text message arrives. The persisted message
      // is the canonical record; the founder-mode pill should disappear
      // because the rendered turn now reflects the full DB content.
      const truncated = { ...s.streamTruncatedByAgent };
      if (message.type === 'assistant_text' && agentId in truncated) {
        delete truncated[agentId];
      }
      return {
        messagesByAgent: { ...s.messagesByAgent, [agentId]: next },
        partialTextByAgent: partial,
        streamTruncatedByAgent: truncated,
      };
    }),

  setMessages: (agentId, messages) =>
    set((s) => ({
      messagesByAgent: { ...s.messagesByAgent, [agentId]: [...messages] },
    })),

  setPartialText: (agentId, text) =>
    set((s) => {
      if (text.length === 0) {
        if (!(agentId in s.partialTextByAgent)) return s;
        const next = { ...s.partialTextByAgent };
        delete next[agentId];
        return { partialTextByAgent: next };
      }
      return {
        partialTextByAgent: { ...s.partialTextByAgent, [agentId]: text },
      };
    }),

  clearPartial: (agentId) =>
    set((s) => {
      if (!(agentId in s.partialTextByAgent)) return s;
      const next = { ...s.partialTextByAgent };
      delete next[agentId];
      return { partialTextByAgent: next };
    }),

  // Sprint 20.3 PR-7 (D013-EC-22) — set / clear the truncation latch.
  setStreamTruncated: (agentId) =>
    set((s) => {
      if (s.streamTruncatedByAgent[agentId]) return s;
      return { streamTruncatedByAgent: { ...s.streamTruncatedByAgent, [agentId]: true } };
    }),
  clearStreamTruncated: (agentId) =>
    set((s) => {
      if (!(agentId in s.streamTruncatedByAgent)) return s;
      const next = { ...s.streamTruncatedByAgent };
      delete next[agentId];
      return { streamTruncatedByAgent: next };
    }),

  setLiveActivity: (agentId, activity) =>
    set((s) => {
      if (activity === null) {
        if (!(agentId in s.liveActivityByAgent)) return s;
        const next = { ...s.liveActivityByAgent };
        delete next[agentId];
        return { liveActivityByAgent: next };
      }
      const existing = s.liveActivityByAgent[agentId];
      if (
        existing &&
        existing.kind === activity.kind &&
        existing.startedAtMs === activity.startedAtMs &&
        (existing.kind !== 'tool' ||
          (activity.kind === 'tool' &&
            existing.toolUseId === activity.toolUseId &&
            existing.label === activity.label))
      ) {
        // Identical event re-emitted (handshake replay) — keep the same
        // object reference so React skips the re-render.
        return s;
      }
      return {
        liveActivityByAgent: { ...s.liveActivityByAgent, [agentId]: activity },
      };
    }),

  clearAllLiveActivity: () =>
    set((s) => {
      if (Object.keys(s.liveActivityByAgent).length === 0) return s;
      return { liveActivityByAgent: {} };
    }),

  setUserScrolled: (agentId, scrolled) =>
    set((s) => ({
      userScrolledByAgent: { ...s.userScrolledByAgent, [agentId]: scrolled },
    })),

  loadMessages: async (agentId) => {
    const r = await api<{
      agent: Agent;
      messages: Message[];
      lastUserMessageId: string | null;
      hasMore: boolean;
    }>(`/api/agents/${encodeURIComponent(agentId)}`);
    set((s) => ({
      agents: { ...s.agents, [r.agent.id]: r.agent },
      messagesByAgent: { ...s.messagesByAgent, [agentId]: r.messages },
      historyByAgent: {
        ...s.historyByAgent,
        [agentId]: {
          hasMore: r.hasMore,
          lastUserMessageId: r.lastUserMessageId,
          loadingOlder: false,
        },
      },
    }));
    // Sprint 15 — sticky anchor invariant. If the smart window dropped the
    // active user message (HIST7 runaway turn), paginate upward until it
    // appears or hasMore is false. The await-then-fire pattern means
    // ensureStickyAnchorLoaded only runs after loadMessages has returned,
    // so callers waiting on loadMessages don't pay the recovery cost.
    void useAppStore.getState().ensureStickyAnchorLoaded(agentId);
  },

  // Sprint 15 — merge older messages at the head of the array, dedupe by
  // id. The existing tail order (where appendMessage adds new live events)
  // is preserved; only the head changes.
  prependMessages: (agentId, older) =>
    set((s) => {
      const existing = s.messagesByAgent[agentId] ?? [];
      if (older.length === 0) return s;
      const existingIds = new Set(existing.map((m) => m.id));
      const fresh = older.filter((m) => !existingIds.has(m.id));
      if (fresh.length === 0) return s;
      return {
        messagesByAgent: {
          ...s.messagesByAgent,
          [agentId]: [...fresh, ...existing],
        },
      };
    }),

  mergeMessages: (agentId, llegados) =>
    set((s) => {
      const existing = s.messagesByAgent[agentId] ?? [];
      const ids = new Set(existing.map((m) => m.id));
      const fresh = llegados.filter((m) => !ids.has(m.id));
      if (fresh.length === 0) return s;
      const enOrden = (a: Message, b: Message) => (a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
      return {
        messagesByAgent: { ...s.messagesByAgent, [agentId]: [...existing, ...fresh].sort(enOrden) },
      };
    }),

  loadOlderMessages: async (agentId) => {
    const state = useAppStore.getState();
    const history = state.historyByAgent[agentId];
    if (!history || !history.hasMore || history.loadingOlder) return;
    const messages = state.messagesByAgent[agentId] ?? [];
    const oldest = messages[0];
    if (!oldest) return;

    set((s) => ({
      historyByAgent: {
        ...s.historyByAgent,
        [agentId]: { ...s.historyByAgent[agentId]!, loadingOlder: true },
      },
    }));

    try {
      const r = await fetchAgentMessages(agentId, {
        beforeTs: oldest.timestamp,
        beforeId: oldest.id,
        limit: 200,
      });
      set((s) => {
        const existing = s.messagesByAgent[agentId] ?? [];
        const existingIds = new Set(existing.map((m) => m.id));
        const fresh = r.messages.filter((m) => !existingIds.has(m.id));
        return {
          messagesByAgent: {
            ...s.messagesByAgent,
            [agentId]: [...fresh, ...existing],
          },
          historyByAgent: {
            ...s.historyByAgent,
            [agentId]: {
              ...s.historyByAgent[agentId]!,
              hasMore: r.hasMore,
              loadingOlder: false,
            },
          },
        };
      });
    } catch (err) {
      // Surface the failure by clearing loadingOlder so a retry is
      // possible. A single failed page does not invalidate the whole
      // stream — the user can scroll up again to retry.
      set((s) => ({
        historyByAgent: {
          ...s.historyByAgent,
          [agentId]: { ...s.historyByAgent[agentId]!, loadingOlder: false },
        },
      }));
      throw err;
    }
  },

  ensureStickyAnchorLoaded: async (agentId) => {
    // Bounded loop. The common case (smart window already includes the
    // active user message) returns on the first check. The runaway-turn
    // case (HIST7) paginates upward at most ENSURE_LOOP_CAP times.
    const ENSURE_LOOP_CAP = 10;
    for (let i = 0; i < ENSURE_LOOP_CAP; i++) {
      const state = useAppStore.getState();
      const history = state.historyByAgent[agentId];
      if (!history) return;
      const anchorId = history.lastUserMessageId;
      if (!anchorId) return;
      const messages = state.messagesByAgent[agentId] ?? [];
      if (messages.some((m) => m.id === anchorId)) return;
      if (!history.hasMore) return;
      await state.loadOlderMessages(agentId);
    }
  },

  resetForTests: () => set({ ...initialState }),
}));

